/**
 * passport.ts — the one goal system. PASSPORT-2.
 *
 *   A goal is one sentence, with rings you close. Each ring is either a
 *   step you tick or one thing Orbit counts (bookings, paid revenue,
 *   new clients, galleries delivered). Close every ring and the world
 *   is stamped.
 *
 * Everything reads `goal_progress` — a goal with its rings and their
 * live values, counted on the server (goal_counter_value) so web, iOS,
 * Nova and the nudge cron all see the same number. Everything writes
 * through the passport_* functions, where the rules live:
 *   · passport_create_goal  — a goal + 1–5 rings, its world chosen once
 *   · passport_tick         — close a ring (a step can reopen while the goal is open)
 *   · passport_sync         — close reached counters, complete the goal
 *   · passport_stamp        — the arrival was played
 *   · passport_archive      — leaves the chart, frees its world
 *
 * Replaces services/planets.ts and services/goals.ts (migration
 * 20260925100000_passport2_goals.sql).
 */
import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Json, Tables } from '@/integrations/supabase/types';
import { useOrganization } from './organizations';
import { useAuth } from '@/hooks/useAuth';
import { useDemoMode } from '@/contexts/DemoModeContext';
import { useDemoStore } from '@/contexts/DemoStoreContext';
import { demoGoals } from '@/data/demoOverlay';

// ── model ──────────────────────────────────────────────────────────

export type RingKind = 'step' | 'counter';
/** The ring's colour, and what kind of work it is. */
export type RingInk = 'bookings' | 'craft' | 'revenue' | 'audience' | 'ops';
/** The five things Orbit counts. */
export type CounterMetric = 'bookings' | 'revenue' | 'clients' | 'galleries' | 'expenses';

export interface GoalRing {
  id: string;
  position: number;
  kind: RingKind;
  label: string;
  ink: RingInk;
  metric: CounterMetric | null;
  target: number | null;
  closed_at: string | null;
  /** counters only — live, capped at target; null for steps */
  value: number | null;
}

export interface Goal {
  id: string;
  org_id: string;
  created_by: string | null;
  title: string;
  why: string | null;
  world_id: string;
  due_on: string | null;
  pinned: boolean;
  completed_at: string | null;
  stamped_at: string | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
  rings: GoalRing[];
  rings_total: number;
  rings_closed: number;
}

export interface RingInput {
  kind: RingKind;
  label?: string;
  ink?: RingInk;
  metric?: CounterMetric;
  target?: number;
}

export interface GoalInput {
  title: string;
  rings: RingInput[];
  why?: string | null;
  due_on?: string | null;
  pinned?: boolean;
}

export const RING_INK_HEX: Record<RingInk, string> = {
  bookings: '#D4541F',
  craft: '#A8C8E8',
  revenue: '#D4A02C',
  audience: '#E8A98D',
  ops: '#5FA3A5',
};

/** What each counter is, in the studio's words. */
export const COUNTERS: Record<CounterMetric, { label: string; ink: RingInk; unit: string; hint: string; money?: boolean }> = {
  bookings: { label: 'Projects booked', ink: 'bookings', unit: 'booked', hint: 'Every project that moves past the lead stage.' },
  revenue: { label: 'Paid', ink: 'revenue', unit: 'paid', hint: 'Every payment that lands in Stripe, refunds netted.', money: true },
  clients: { label: 'New clients', ink: 'audience', unit: 'clients', hint: 'Every client you add.' },
  galleries: { label: 'Galleries delivered', ink: 'craft', unit: 'delivered', hint: 'Every gallery you create.' },
  // Counts UP to the target like every other counter ("put $2,000
  // into glass this year") — it is not a spending ceiling.
  expenses: { label: 'Spent', ink: 'ops', unit: 'spent', hint: 'Everything you log under Expenses.', money: true },
};

/** A `goal_progress` row (every column nullable, as views are typed) → Goal. */
type ProgressRow = Tables<'goal_progress'>;
export function goalFromRow(row: ProgressRow): Goal {
  const rings = (Array.isArray(row.rings) ? (row.rings as unknown as GoalRing[]) : []).slice().sort((a, b) => a.position - b.position);
  return {
    id: row.id ?? '',
    org_id: row.org_id ?? '',
    created_by: row.created_by,
    title: row.title ?? '',
    why: row.why,
    world_id: row.world_id ?? 'dunes',
    due_on: row.due_on,
    pinned: !!row.pinned,
    completed_at: row.completed_at,
    stamped_at: row.stamped_at,
    archived_at: row.archived_at,
    created_at: row.created_at ?? '',
    updated_at: row.updated_at ?? row.created_at ?? '',
    rings,
    rings_total: Number(row.rings_total ?? rings.length),
    rings_closed: Number(row.rings_closed ?? rings.filter((r) => r.closed_at).length),
  };
}

export const ringDone = (r: GoalRing) => r.closed_at !== null;
export const ringProgress = (r: GoalRing) => (ringDone(r) ? 1 : r.kind === 'counter' && r.target ? Math.min(1, Math.max(0, (r.value ?? 0) / r.target)) : 0);
export const goalDone = (g: Goal) => g.completed_at !== null;
/** finished, arrival not yet played */
export const goalPendingStamp = (g: Goal) => g.completed_at !== null && g.stamped_at === null;
export const goalLive = (g: Goal) => g.archived_at === null;
/**
 * Every ring reached, but the server hasn't closed the goal.
 *
 * `completed_at` is written by `passport_sync` alone, and nothing on a
 * read path calls it — only create, tick, stamp and the nightly cron
 * (`passport-nudge-cron`). So a COUNTER goal that reaches its target
 * through ordinary work (a booking lands, an invoice is paid) stays
 * `completed_at: null` until one of those fires.
 *
 * Galaxy3D and PassportTodayCard each paper over that client-side with
 * their own `ringProgress >= 1` fallback; PinnedGoalsCard filters on
 * `!g.completed_at` and doesn't, so a finished goal sat in the Goals
 * card showing "12 of 12" indefinitely. Three surfaces guessing at the
 * same truth is how they came to disagree — so ask the server instead.
 */
const reachedButOpen = (g: Goal) =>
  g.completed_at === null && g.rings.length > 0 && g.rings.every((r) => ringProgress(r) >= 1);

/** Goals already asked about this page load, so a server that declines
 *  to close one (a step still open, a permissions refusal) can't put us
 *  in a refetch loop. */
const syncAsked = new Set<string>();

export function formatCount(metric: CounterMetric | null, n: number) {
  return metric && COUNTERS[metric].money ? `$${Math.round(n).toLocaleString()}` : String(Math.round(n));
}
/** "3 of 12 booked" / "$18,500 of $50,000 paid" */
export function ringLine(r: GoalRing) {
  if (r.kind !== 'counter' || !r.metric || !r.target) return r.label;
  return `${formatCount(r.metric, r.value ?? 0)} of ${formatCount(r.metric, r.target)} ${COUNTERS[r.metric].unit}`;
}

// ── reads ──────────────────────────────────────────────────────────

const KEY = ['goals'] as const;

/** The read itself. Wrapped by `useGoals` below, which also heals a
 *  goal the server hasn't closed yet — call that one. */
const useGoalsQuery = (opts: { includeArchived?: boolean } = {}) => {
  const { data: org } = useOrganization();
  const { user } = useAuth();
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();
  return useQuery({
    queryKey: [...KEY, org?.id, isDemo, isDemo ? demoStore.version : 0, !!opts.includeArchived],
    queryFn: async (): Promise<Goal[]> => {
      let rows: Goal[];
      if (isDemo) {
        const list = demoStore.goals.list();
        rows = ((list.length > 0 ? list : demoGoals) as unknown as Goal[]).map((g) => ({ ...g, rings: [...(g.rings ?? [])].sort((a, b) => a.position - b.position), rings_total: Number(g.rings_total ?? g.rings?.length ?? 0), rings_closed: Number(g.rings_closed ?? (g.rings ?? []).filter((r) => r.closed_at).length) }));
      } else {
        if (!org?.id) return [];
        const { data, error } = await supabase
          .from('goal_progress')
          .select('*')
          .eq('org_id', org.id)
          .order('created_at', { ascending: true });
        if (error) throw error;
        rows = (data ?? []).map(goalFromRow);
      }
      return opts.includeArchived ? rows : rows.filter(goalLive);
    },
    enabled: isDemo || (!!user && !!org?.id),
  });
};

/**
 * `useGoals`, plus: anything reached-but-open gets a `passport_sync` so
 * the database agrees with the arithmetic. One place, every surface.
 */
export const useGoals = (opts: { includeArchived?: boolean } = {}) => {
  const query = useGoalsQuery(opts);
  const queryClient = useQueryClient();
  const { isDemo } = useDemoMode();

  useEffect(() => {
    if (isDemo || !query.data) return;
    const stale = query.data.filter(reachedButOpen).filter((g) => !syncAsked.has(g.id));
    if (stale.length === 0) return;
    stale.forEach((g) => syncAsked.add(g.id));
    void Promise.all(stale.map((g) => supabase.rpc('passport_sync', { p_goal_id: g.id })))
      .then(() => queryClient.invalidateQueries({ queryKey: KEY }))
      .catch(() => {
        // Best-effort healing. The client-side fallbacks still read
        // right, and the nightly cron will close it.
      });
  }, [query.data, isDemo, queryClient]);

  return query;
};

/** Live updates: any change to a goal or a ring refetches the chart. */
export const useGoalsRealtime = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { isDemo } = useDemoMode();
  useEffect(() => {
    if (!user || isDemo) return;
    const bump = () => { void queryClient.invalidateQueries({ queryKey: KEY }); };
    const channel = supabase
      .channel('passport-live-web')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'goals' }, bump)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'goal_rings' }, bump)
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
  }, [user, isDemo, queryClient]);
};

// ── writes ─────────────────────────────────────────────────────────

const nowIso = () => new Date().toISOString();

/** Demo mode: the same rules, in memory. */
function demoSync(g: Goal): Goal {
  const rings = g.rings.map((r) => (r.kind === 'counter' && !r.closed_at && r.target && (r.value ?? 0) >= r.target ? { ...r, closed_at: nowIso() } : r));
  const allClosed = rings.length > 0 && rings.every((r) => r.closed_at);
  return { ...g, rings, rings_closed: rings.filter((r) => r.closed_at).length, rings_total: rings.length, completed_at: g.completed_at ?? (allClosed ? nowIso() : null) };
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: KEY });
}

/** One goal, with its rings and live values, straight from the view. */
export async function fetchGoal(id: string): Promise<Goal | null> {
  const { data, error } = await supabase.from('goal_progress').select('*').eq('id', id).maybeSingle();
  if (error) throw error;
  return data ? goalFromRow(data) : null;
}

/** Create a goal with its rings; resolves to the goal as the server sees it (world chosen). */
export const useCreateGoal = () => {
  const invalidate = useInvalidate();
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();
  const { data: org } = useOrganization();
  return useMutation({
    mutationFn: async (input: GoalInput): Promise<Goal> => {
      const rings = input.rings.slice(0, 5).map((r, i) => ({
        kind: r.kind,
        label: r.label?.trim() || (r.kind === 'counter' && r.metric ? COUNTERS[r.metric].label : 'Done'),
        ink: r.ink ?? (r.kind === 'counter' && r.metric ? COUNTERS[r.metric].ink : 'ops'),
        metric: r.kind === 'counter' ? r.metric : undefined,
        target: r.kind === 'counter' ? r.target : undefined,
        position: i,
      }));
      if (rings.length === 0) throw new Error('A goal needs at least one ring.');
      if (isDemo) {
        const id = `demo-goal-${crypto.randomUUID().slice(0, 8)}`;
        const taken = new Set((demoStore.goals.list() as unknown as Goal[]).filter(goalLive).map((g) => g.world_id));
        const { ALL_WORLDS } = await import('@/components/galaxy3d/worlds');
        const world = ALL_WORLDS.find((w) => !taken.has(w.id))?.id ?? 'dunes';
        const row: Goal = {
          id, org_id: 'demo-org', created_by: 'demo-studio-user', title: input.title.trim(), why: input.why?.trim() || null, world_id: world,
          due_on: input.due_on ?? null, pinned: !!input.pinned, completed_at: null, stamped_at: null, archived_at: null, created_at: nowIso(), updated_at: nowIso(),
          rings: rings.map((r) => ({ id: `${id}-r${r.position}`, position: r.position, kind: r.kind, label: r.label, ink: r.ink, metric: r.metric ?? null, target: r.target ?? null, closed_at: null, value: r.kind === 'counter' ? 0 : null })),
          rings_total: rings.length, rings_closed: 0,
        };
        demoStore.goals.add(row as never);
        return row;
      }
      if (!org?.id) throw new Error('Your studio isn’t set up yet');
      const { data, error } = await supabase.rpc('passport_create_goal', {
        p_title: input.title.trim(),
        p_rings: rings as unknown as Json,
        p_why: input.why?.trim() || undefined,
        p_due_on: input.due_on ?? undefined,
        p_pinned: !!input.pinned,
      });
      if (error) throw error;
      const id = data;
      const goal = await fetchGoal(id);
      if (!goal) throw new Error('The goal was created but could not be read back.');
      return goal;
    },
    onSuccess: () => { void invalidate(); },
  });
};

export interface GoalPatch { title?: string; why?: string | null; due_on?: string | null; pinned?: boolean }
export const useUpdateGoal = () => {
  const invalidate = useInvalidate();
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();
  return useMutation({
    mutationFn: async ({ id, ...patch }: { id: string } & GoalPatch) => {
      if (isDemo) { demoStore.goals.update(id, patch as never); return; }
      const { error } = await supabase.from('goals').update(patch).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => { void invalidate(); },
  });
};

/** Close a ring (or reopen a step while the goal is still open). */
export const useTickRing = () => {
  const invalidate = useInvalidate();
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();
  return useMutation({
    mutationFn: async ({ goalId, ringId, closed = true }: { goalId: string; ringId: string; closed?: boolean }) => {
      if (isDemo) {
        const g = (demoStore.goals.list() as unknown as Goal[]).find((x) => x.id === goalId);
        if (!g) return;
        if (!closed && g.completed_at) throw new Error('A finished world stays finished.');
        const rings = g.rings.map((r) => (r.id === ringId ? (closed ? { ...r, closed_at: r.closed_at ?? nowIso() } : r.kind === 'step' ? { ...r, closed_at: null } : r) : r));
        demoStore.goals.update(goalId, demoSync({ ...g, rings }) as never);
        return;
      }
      const { error } = await supabase.rpc('passport_tick', { p_ring_id: ringId, p_closed: closed });
      if (error) throw error;
    },
    onSuccess: () => { void invalidate(); },
  });
};

/** Ask the server to close reached counters + complete the goal. */
export const useSyncGoal = () => {
  const invalidate = useInvalidate();
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();
  return useMutation({
    mutationFn: async (goalId: string) => {
      if (isDemo) { const g = (demoStore.goals.list() as unknown as Goal[]).find((x) => x.id === goalId); if (g) demoStore.goals.update(goalId, demoSync(g) as never); return; }
      const { error } = await supabase.rpc('passport_sync', { p_goal_id: goalId });
      if (error) throw error;
    },
    onSuccess: () => { void invalidate(); },
  });
};

/** The arrival was played — press the stamp. */
export const useStampGoal = () => {
  const invalidate = useInvalidate();
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();
  return useMutation({
    mutationFn: async (goalId: string) => {
      if (isDemo) {
        const g = (demoStore.goals.list() as unknown as Goal[]).find((x) => x.id === goalId);
        if (g) { const s = demoSync(g); demoStore.goals.update(goalId, { ...s, stamped_at: s.completed_at ? s.stamped_at ?? nowIso() : null } as never); }
        return;
      }
      const { error } = await supabase.rpc('passport_stamp', { p_goal_id: goalId });
      if (error) throw error;
    },
    onSuccess: () => { void invalidate(); },
  });
};

export const useArchiveGoal = () => {
  const invalidate = useInvalidate();
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();
  return useMutation({
    mutationFn: async (goalId: string) => {
      if (isDemo) { demoStore.goals.update(goalId, { archived_at: nowIso(), pinned: false } as never); return; }
      const { error } = await supabase.rpc('passport_archive', { p_goal_id: goalId });
      if (error) throw error;
    },
    onSuccess: () => { void invalidate(); },
  });
};
